import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { ReplaceStep } from '@tiptap/pm/transform';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
export const SCENE_HEADING_DOM_CLASS = 'scribane-scene-heading';
export const SCRIPT_CHARACTER_CUE_DOM_CLASS = 'scribane-script-character-cue';
export const SCRIPT_DIALOGUE_BLOCK_DOM_CLASS = 'scribane-script-dialogue-block';
export const SCRIPT_PARENTHETICAL_DOM_CLASS = 'scribane-script-parenthetical';
export const SCENE_HEADING_PREFIXES = ['INT.', 'EXT.'];
export function isSceneHeadingParagraphText(text) {
    const trimmed = text.trimStart().toLocaleUpperCase();
    return SCENE_HEADING_PREFIXES.some((prefix) => trimmed.startsWith(prefix));
}
export function isScriptParentheticalParagraphText(text) {
    return text.trimStart().startsWith('(');
}
function dialogueOrParentheticalClassForParagraph(node) {
    if (isScriptParentheticalParagraphText(node.textContent)) {
        return SCRIPT_PARENTHETICAL_DOM_CLASS;
    }
    return SCRIPT_DIALOGUE_BLOCK_DOM_CLASS;
}
export function isSelectionInSceneHeadingParagraph(state) {
    const { $from } = state.selection;
    for (let depth = $from.depth; depth > 0; depth -= 1) {
        const node = $from.node(depth);
        if (node.isTextblock) {
            return isSceneHeadingParagraphText(node.textContent);
        }
    }
    if ($from.parent.isTextblock) {
        return isSceneHeadingParagraphText($from.parent.textContent);
    }
    return false;
}
export function getSceneHeadingParagraphStartPosAtSelection(state) {
    const { $from } = state.selection;
    for (let depth = $from.depth; depth > 0; depth -= 1) {
        const node = $from.node(depth);
        if (node.isTextblock && isSceneHeadingParagraphText(node.textContent)) {
            return $from.before(depth);
        }
    }
    if ($from.parent.isTextblock && isSceneHeadingParagraphText($from.parent.textContent)) {
        return $from.before($from.depth);
    }
    return null;
}
function paragraphStartsWithCharacterMention(node) {
    for (let i = 0; i < node.childCount; i += 1) {
        const child = node.child(i);
        if (child.isText) {
            if (child.text && child.text.trim().length > 0) {
                return false;
            }
            continue;
        }
        if (child.type.name === 'scriptMention') {
            return child.attrs.entityType === 'character';
        }
        return false;
    }
    return false;
}
function isParagraphEmpty(node) {
    if (paragraphStartsWithCharacterMention(node)) {
        return false;
    }
    return node.textContent.trim().length === 0;
}
function positionOfTopLevelIndex(doc, index) {
    let pos = 0;
    for (let i = 0; i < index; i += 1) {
        pos += doc.child(i).nodeSize;
    }
    return pos;
}
function topLevelIndexNear(doc, pos) {
    if (doc.childCount === 0) {
        return 0;
    }
    const clamped = Math.max(0, Math.min(pos, doc.content.size));
    const ref = clamped >= doc.content.size && clamped > 0 ? clamped - 1 : clamped;
    const index = doc.resolve(ref).index(0);
    return Math.max(0, Math.min(index, doc.childCount - 1));
}
function mergeDocRanges(ranges) {
    if (ranges.length === 0) {
        return [];
    }
    const sorted = [...ranges].sort((a, b) => a.from - b.from);
    const merged = [{ ...sorted[0] }];
    for (let i = 1; i < sorted.length; i += 1) {
        const current = sorted[i];
        const last = merged[merged.length - 1];
        if (current.from <= last.to) {
            last.to = Math.max(last.to, current.to);
        }
        else {
            merged.push({ ...current });
        }
    }
    return merged;
}
function getTransactionNewDocRanges(tr) {
    if (!tr.docChanged) {
        return [];
    }
    const ranges = [];
    try {
        tr.mapping.maps.forEach((stepMap, index) => {
            stepMap.forEach((_oldStart, _oldEnd, newStart, newEnd) => {
                let from = newStart;
                let to = newEnd;
                for (let i = index + 1; i < tr.mapping.maps.length; i += 1) {
                    from = tr.mapping.maps[i].map(from, -1);
                    to = tr.mapping.maps[i].map(to, 1);
                }
                ranges.push({ from, to });
            });
        });
    }
    catch {
        return 'full';
    }
    if (ranges.length === 0) {
        return [];
    }
    return mergeDocRanges(ranges);
}
function expandToTopLevelBlockRange(doc, from, to) {
    if (doc.childCount === 0) {
        return null;
    }
    let rangeFrom = null;
    let rangeTo = 0;
    let nodeStart = 0;
    for (let index = 0; index < doc.childCount; index += 1) {
        if (nodeStart > to) {
            break;
        }
        const nodeEnd = nodeStart + doc.child(index).nodeSize;
        if (nodeEnd >= from) {
            if (rangeFrom === null) {
                rangeFrom = nodeStart;
            }
            rangeTo = nodeEnd;
        }
        nodeStart = nodeEnd;
    }
    return rangeFrom === null ? null : { from: rangeFrom, to: rangeTo };
}
function expandToCueBlockRange(doc, from, to, dialogueEndBeforePosByCuePos) {
    if (doc.childCount === 0) {
        return null;
    }
    const clampedFrom = Math.max(0, Math.min(from, doc.content.size));
    const clampedTo = Math.max(clampedFrom, Math.min(to, doc.content.size));
    let startIndex = topLevelIndexNear(doc, clampedFrom);
    let endIndex = topLevelIndexNear(doc, Math.max(clampedFrom, clampedTo - 1));
    while (startIndex > 0) {
        const node = doc.child(startIndex);
        if (node.type.name === 'paragraph' && paragraphStartsWithCharacterMention(node)) {
            break;
        }
        if (node.type.name === 'paragraph' && isSceneHeadingParagraphText(node.textContent)) {
            const headingStart = positionOfTopLevelIndex(doc, startIndex);
            const headingEnd = headingStart + node.nodeSize;
            if (headingEnd <= clampedFrom) {
                if (startIndex < doc.childCount - 1) {
                    startIndex += 1;
                }
                break;
            }
            startIndex -= 1;
            continue;
        }
        startIndex -= 1;
    }
    if (startIndex < 0 || startIndex >= doc.childCount) {
        return null;
    }
    const cuePos = positionOfTopLevelIndex(doc, startIndex);
    const cueNode = doc.child(startIndex);
    const stickyEnd = cueNode.type.name === 'paragraph'
        && paragraphStartsWithCharacterMention(cueNode)
        ? dialogueEndBeforePosByCuePos?.get(cuePos)
        : undefined;
    const changeEndIndex = endIndex;
    if (stickyEnd !== undefined) {
        const stickyIndex = topLevelIndexNear(doc, stickyEnd);
        endIndex = Math.min(stickyIndex + 1, doc.childCount - 1);
    }
    else {
        const forwardHorizon = changeEndIndex + 4;
        while (endIndex < doc.childCount - 1) {
            const next = doc.child(endIndex + 1);
            if (next.type.name === 'footnotes') {
                break;
            }
            if (next.type.name === 'paragraph' && paragraphStartsWithCharacterMention(next)) {
                break;
            }
            if (next.type.name === 'paragraph' && isSceneHeadingParagraphText(next.textContent)) {
                break;
            }
            endIndex += 1;
            if (endIndex > changeEndIndex) {
                const node = doc.child(endIndex);
                if (node.type.name === 'paragraph' && isParagraphEmpty(node)) {
                    if (endIndex < doc.childCount - 1) {
                        endIndex += 1;
                    }
                    break;
                }
                if (endIndex >= forwardHorizon) {
                    break;
                }
            }
        }
    }
    if (startIndex > endIndex) {
        return null;
    }
    const rangeFrom = cuePos;
    const rangeTo = positionOfTopLevelIndex(doc, endIndex) + doc.child(endIndex).nodeSize;
    return { from: rangeFrom, to: rangeTo };
}
function paragraphNodeContainingPos(doc, pos) {
    if (doc.content.size === 0) {
        return null;
    }
    const ref = Math.max(0, Math.min(pos, doc.content.size - 1));
    const $pos = doc.resolve(ref);
    for (let depth = $pos.depth; depth > 0; depth -= 1) {
        const node = $pos.node(depth);
        if (node.type.name === 'paragraph') {
            return node;
        }
    }
    return null;
}
function isInPlaceTextEditThatCannotChangeScriptRoles(tr, oldDoc, newDoc) {
    if (!tr.docChanged || tr.steps.length === 0) {
        return true;
    }
    if (newDoc.childCount !== oldDoc.childCount) {
        return false;
    }
    for (let i = 0; i < tr.steps.length; i += 1) {
        const step = tr.steps[i];
        if (!(step instanceof ReplaceStep)) {
            return false;
        }
        if (step.slice.openStart !== 0 || step.slice.openEnd !== 0) {
            return false;
        }
        const $from = oldDoc.resolve(step.from);
        if (!$from.parent.isTextblock || $from.parent.type.name !== 'paragraph') {
            return false;
        }
        const blockStart = $from.start($from.depth);
        const blockEnd = $from.end($from.depth);
        if (step.from < blockStart || step.to > blockEnd) {
            return false;
        }
        if (step.from === blockStart) {
            return false;
        }
        if (isParagraphEmpty($from.parent)) {
            return false;
        }
        const mappedInside = tr.mapping.slice(i).map(Math.min(step.from, step.to), -1);
        const newParagraph = paragraphNodeContainingPos(newDoc, mappedInside);
        if (!newParagraph || isParagraphEmpty(newParagraph)) {
            return false;
        }
        if (isSceneHeadingParagraphText($from.parent.textContent)
            !== isSceneHeadingParagraphText(newParagraph.textContent)) {
            return false;
        }
        if (isScriptParentheticalParagraphText($from.parent.textContent)
            !== isScriptParentheticalParagraphText(newParagraph.textContent)) {
            return false;
        }
    }
    return true;
}
function scriptDecorationClassAt(decoSet, pos, node) {
    const end = pos + node.nodeSize;
    const found = decoSet.find(pos, end);
    for (const decoration of found) {
        if (decoration.from !== pos || decoration.to !== end) {
            continue;
        }
        const attrs = decoration.type?.attrs;
        const cls = attrs?.class;
        if (typeof cls !== 'string') {
            continue;
        }
        if (cls === SCRIPT_CHARACTER_CUE_DOM_CLASS
            || cls === SCRIPT_DIALOGUE_BLOCK_DOM_CLASS
            || cls === SCRIPT_PARENTHETICAL_DOM_CLASS) {
            return cls;
        }
    }
    return null;
}
function stripDialogueDecorationsAfterPos(doc, decoSet, afterPos) {
    const afterNode = doc.nodeAt(afterPos);
    if (!afterNode) {
        return decoSet;
    }
    let stripFrom = afterPos + afterNode.nodeSize;
    if (stripFrom >= doc.content.size) {
        return decoSet;
    }
    let index = topLevelIndexNear(doc, stripFrom);
    if (positionOfTopLevelIndex(doc, index) < stripFrom) {
        if (index >= doc.childCount - 1) {
            return decoSet;
        }
        index += 1;
        stripFrom = positionOfTopLevelIndex(doc, index);
    }
    let stripTo = stripFrom;
    while (index < doc.childCount) {
        const child = doc.child(index);
        if (child.type.name === 'footnotes') {
            break;
        }
        if (child.type.name === 'paragraph' && paragraphStartsWithCharacterMention(child)) {
            break;
        }
        if (child.type.name === 'paragraph' && isSceneHeadingParagraphText(child.textContent)) {
            break;
        }
        stripTo = positionOfTopLevelIndex(doc, index) + child.nodeSize;
        index += 1;
    }
    if (stripTo <= stripFrom) {
        return decoSet;
    }
    return removeDecorationsAnchoredInRange(decoSet, stripFrom, stripTo, doc.content.size);
}
function tryDecorateParagraphSplit(tr, oldDoc, newDoc, mappedDecos) {
    if (tr.steps.length !== 1 || newDoc.childCount !== oldDoc.childCount + 1) {
        return null;
    }
    const step = tr.steps[0];
    if (!(step instanceof ReplaceStep)) {
        return null;
    }
    const newParaPos = tr.mapping.map(step.from, 1);
    const newPara = newDoc.nodeAt(newParaPos);
    if (!newPara || newPara.type.name !== 'paragraph' || newParaPos === 0) {
        return null;
    }
    const prev = newDoc.resolve(newParaPos).nodeBefore;
    if (!prev || prev.type.name !== 'paragraph') {
        return null;
    }
    const prevPos = newParaPos - prev.nodeSize;
    if (paragraphStartsWithCharacterMention(newPara) || isSceneHeadingParagraphText(newPara.textContent)) {
        return null;
    }
    const prevClass = scriptDecorationClassAt(mappedDecos, prevPos, prev);
    const newlyDiscoveredEnds = new Map();
    let decos = mappedDecos;
    if (isParagraphEmpty(prev) && isParagraphEmpty(newPara)) {
        let index = topLevelIndexNear(newDoc, prevPos);
        while (index >= 0) {
            const node = newDoc.child(index);
            if (node.type.name === 'paragraph' && paragraphStartsWithCharacterMention(node)) {
                const cuePos = positionOfTopLevelIndex(newDoc, index);
                newlyDiscoveredEnds.set(cuePos, prevPos);
                decos = removeDecorationsAnchoredInRange(decos, prevPos, newParaPos + newPara.nodeSize, newDoc.content.size);
                decos = stripDialogueDecorationsAfterPos(newDoc, decos, prevPos);
                return { decorations: decos, newlyDiscoveredEnds };
            }
            if (node.type.name === 'paragraph' && isSceneHeadingParagraphText(node.textContent)) {
                break;
            }
            index -= 1;
        }
        return { decorations: decos, newlyDiscoveredEnds };
    }
    const continuesScriptBlock = prevClass === SCRIPT_CHARACTER_CUE_DOM_CLASS
        || prevClass === SCRIPT_DIALOGUE_BLOCK_DOM_CLASS
        || prevClass === SCRIPT_PARENTHETICAL_DOM_CLASS
        || paragraphStartsWithCharacterMention(prev);
    if (!continuesScriptBlock) {
        return { decorations: decos, newlyDiscoveredEnds };
    }
    const className = dialogueOrParentheticalClassForParagraph(newPara);
    decos = decos.add(newDoc, [
        Decoration.node(newParaPos, newParaPos + newPara.nodeSize, { class: className }),
    ]);
    return { decorations: decos, newlyDiscoveredEnds };
}
function collectParagraphsInRange(doc, from, to) {
    const paragraphs = [];
    doc.nodesBetween(from, to, (node, pos) => {
        if (node.type.name === 'paragraph') {
            if (pos >= from && pos < to) {
                paragraphs.push({ pos, node });
            }
            return false;
        }
        return true;
    });
    return paragraphs;
}
function buildCharacterCueAndDialogueDecorationList(paragraphs, dialogueEndBeforePosByCuePos, doc) {
    const decorations = [];
    const newlyDiscoveredEnds = new Map();
    for (let i = 0; i < paragraphs.length; i += 1) {
        const { pos, node } = paragraphs[i];
        if (!paragraphStartsWithCharacterMention(node)) {
            continue;
        }
        decorations.push(Decoration.node(pos, pos + node.nodeSize, { class: SCRIPT_CHARACTER_CUE_DOM_CLASS }));
        const endBeforePos = dialogueEndBeforePosByCuePos.get(pos);
        const endNode = endBeforePos === undefined ? null : doc.nodeAt(endBeforePos);
        const hasValidStickyEnd = endBeforePos !== undefined
            && endNode !== null
            && endNode.type.name === 'paragraph'
            && isParagraphEmpty(endNode);
        if (hasValidStickyEnd) {
            let j = i + 1;
            while (j < paragraphs.length) {
                const { pos: pPos, node: pNode } = paragraphs[j];
                if (pPos >= endBeforePos) {
                    break;
                }
                if (paragraphStartsWithCharacterMention(pNode)) {
                    break;
                }
                if (isSceneHeadingParagraphText(pNode.textContent)) {
                    break;
                }
                decorations.push(Decoration.node(pPos, pPos + pNode.nodeSize, {
                    class: dialogueOrParentheticalClassForParagraph(pNode),
                }));
                j += 1;
            }
            continue;
        }
        let j = i + 1;
        while (j < paragraphs.length) {
            const { pos: pPos, node: pNode } = paragraphs[j];
            if (paragraphStartsWithCharacterMention(pNode)) {
                break;
            }
            if (isSceneHeadingParagraphText(pNode.textContent)) {
                break;
            }
            if (isParagraphEmpty(pNode)) {
                if (j + 1 < paragraphs.length && isParagraphEmpty(paragraphs[j + 1].node)) {
                    newlyDiscoveredEnds.set(pos, pPos);
                    break;
                }
                if (j + 1 < paragraphs.length) {
                    const nextNode = paragraphs[j + 1].node;
                    if (!isParagraphEmpty(nextNode) &&
                        !paragraphStartsWithCharacterMention(nextNode) &&
                        !isSceneHeadingParagraphText(nextNode.textContent)) {
                        newlyDiscoveredEnds.set(pos, pPos);
                        break;
                    }
                }
            }
            decorations.push(Decoration.node(pPos, pPos + pNode.nodeSize, {
                class: dialogueOrParentheticalClassForParagraph(pNode),
            }));
            j += 1;
        }
    }
    return { decorations, newlyDiscoveredEnds };
}
function buildCharacterCueAndDialogueDecorations(doc, dialogueEndBeforePosByCuePos) {
    const paragraphs = collectParagraphsInRange(doc, 0, doc.content.size);
    const { decorations, newlyDiscoveredEnds } = buildCharacterCueAndDialogueDecorationList(paragraphs, dialogueEndBeforePosByCuePos, doc);
    return {
        decorations: DecorationSet.create(doc, decorations),
        newlyDiscoveredEnds,
    };
}
function removeDecorationsAnchoredInRange(decoSet, from, to, docSize) {
    const stale = decoSet.find(from, to).filter((decoration) => {
        if (to >= docSize) {
            return decoration.from >= from && decoration.from <= to;
        }
        return decoration.from >= from && decoration.from < to;
    });
    if (stale.length === 0) {
        return decoSet;
    }
    return decoSet.remove(stale);
}
function rebuildCharacterCueDialogueDecorationsInRanges(doc, mappedDecos, dialogueEndBeforePosByCuePos, ranges) {
    let decos = mappedDecos;
    const newlyDiscoveredEnds = new Map();
    const docSize = doc.content.size;
    for (const range of ranges) {
        const expanded = expandToCueBlockRange(doc, range.from, range.to, dialogueEndBeforePosByCuePos);
        if (!expanded) {
            continue;
        }
        const paragraphs = collectParagraphsInRange(doc, expanded.from, expanded.to);
        const hasCue = paragraphs.some(({ node }) => paragraphStartsWithCharacterMention(node));
        const existingInRange = decos.find(expanded.from, expanded.to);
        if (!hasCue && existingInRange.length === 0) {
            continue;
        }
        decos = removeDecorationsAnchoredInRange(decos, expanded.from, expanded.to, docSize);
        const built = buildCharacterCueAndDialogueDecorationList(paragraphs, dialogueEndBeforePosByCuePos, doc);
        if (built.decorations.length > 0) {
            decos = decos.add(doc, built.decorations);
        }
        for (const [cuePos, endPos] of built.newlyDiscoveredEnds) {
            newlyDiscoveredEnds.set(cuePos, endPos);
        }
    }
    return { decorations: decos, newlyDiscoveredEnds };
}
function mapDialogueEndBeforePosByCuePos(map, tr) {
    const next = new Map();
    if (!tr.docChanged) {
        return map;
    }
    for (const [cuePos, endPos] of map) {
        const cueResult = tr.mapping.mapResult(cuePos, -1);
        const endResult = tr.mapping.mapResult(endPos, -1);
        if (cueResult.deleted || endResult.deleted) {
            continue;
        }
        next.set(cueResult.pos, endResult.pos);
    }
    return next;
}
function pruneDialogueEndsByCueExistence(map, doc) {
    const next = new Map();
    for (const [cuePos, endPos] of map) {
        try {
            const node = doc.nodeAt(cuePos);
            if (node && node.type.name === 'paragraph' && paragraphStartsWithCharacterMention(node)) {
                const endNode = doc.nodeAt(endPos);
                if (endNode
                    && endNode.type.name === 'paragraph'
                    && isParagraphEmpty(endNode)) {
                    next.set(cuePos, endPos);
                }
            }
        }
        catch {
        }
    }
    return next;
}
export function buildCharacterCueAndDialogueDecorationsForTest(doc, dialogueEndBeforePosByCuePos) {
    return buildCharacterCueAndDialogueDecorations(doc, dialogueEndBeforePosByCuePos);
}
function isParagraphInsideFootnote(doc, pos) {
    const $pos = doc.resolve(pos);
    for (let d = $pos.depth; d > 0; d -= 1) {
        if ($pos.node(d).type.name === 'footnote') {
            return true;
        }
    }
    return false;
}
function isParagraphInList(doc, pos) {
    const $pos = doc.resolve(pos);
    for (let d = $pos.depth; d > 0; d -= 1) {
        const name = $pos.node(d).type.name;
        if (name === 'listItem' || name === 'taskItem') {
            return true;
        }
    }
    return false;
}
function paragraphHasDecorationClass(decoSet, pos, node, className) {
    const end = pos + node.nodeSize;
    const found = decoSet.find(pos, end);
    return found.some((dec) => {
        if (dec.from !== pos || dec.to !== end) {
            return false;
        }
        const attrs = dec.type?.attrs;
        const cls = attrs?.class;
        if (typeof cls !== 'string') {
            return false;
        }
        return cls.split(/\s+/).includes(className);
    });
}
export function getParagraphExportLayouts(doc) {
    const layouts = [];
    const { decorations } = buildCharacterCueAndDialogueDecorationsForTest(doc, new Map());
    doc.descendants((node, pos) => {
        if (node.type.name !== 'paragraph') {
            return;
        }
        if (isParagraphInsideFootnote(doc, pos)) {
            return;
        }
        if (isParagraphInList(doc, pos)) {
            layouts.push({ role: 'body', indentFirstLine: false });
            return;
        }
        if (isSceneHeadingParagraphText(node.textContent)) {
            layouts.push({ role: 'scene_heading', indentFirstLine: false });
            return;
        }
        if (paragraphStartsWithCharacterMention(node)) {
            layouts.push({ role: 'character_cue', indentFirstLine: false });
            return;
        }
        if (paragraphHasDecorationClass(decorations, pos, node, SCRIPT_PARENTHETICAL_DOM_CLASS)) {
            layouts.push({ role: 'parenthetical', indentFirstLine: false });
            return;
        }
        if (paragraphHasDecorationClass(decorations, pos, node, SCRIPT_DIALOGUE_BLOCK_DOM_CLASS)) {
            layouts.push({ role: 'dialogue', indentFirstLine: false });
            return;
        }
        layouts.push({ role: 'body', indentFirstLine: true });
    });
    return layouts;
}
export function getParagraphScriptRolesForExport(doc) {
    return getParagraphExportLayouts(doc).map((layout) => layout.role);
}
export function getParagraphFirstLineIndentFlagsForExport(doc) {
    return getParagraphExportLayouts(doc).map((layout) => layout.indentFirstLine);
}
export const DIALOGUE_WORDS_PER_MINUTE = 160;
export function estimateDialogueMinutes(wordCount) {
    if (wordCount <= 0) {
        return 0;
    }
    return Math.ceil(wordCount / DIALOGUE_WORDS_PER_MINUTE);
}
function getContentEndBeforeFootnotes(doc) {
    let offset = 0;
    for (let index = 0; index < doc.childCount; index += 1) {
        const child = doc.child(index);
        if (child.type.name === 'footnotes') {
            return offset;
        }
        offset += child.nodeSize;
    }
    return doc.content.size;
}
export function getSceneRange(doc, sceneHeadingPos) {
    const node = doc.nodeAt(sceneHeadingPos);
    if (!node
        || node.type.name !== 'paragraph'
        || !isSceneHeadingParagraphText(node.textContent)) {
        return null;
    }
    const headingIndex = topLevelIndexNear(doc, sceneHeadingPos);
    if (positionOfTopLevelIndex(doc, headingIndex) !== sceneHeadingPos) {
        return null;
    }
    const from = sceneHeadingPos;
    let to = getContentEndBeforeFootnotes(doc);
    for (let i = headingIndex + 1; i < doc.childCount; i += 1) {
        const candidate = doc.child(i);
        if (candidate.type.name === 'footnotes') {
            to = Math.min(to, positionOfTopLevelIndex(doc, i));
            break;
        }
        if (candidate.type.name === 'paragraph'
            && isSceneHeadingParagraphText(candidate.textContent)) {
            to = positionOfTopLevelIndex(doc, i);
            break;
        }
    }
    return { from, to };
}
export function findSceneHeadingPosContainingPos(doc, pos) {
    if (doc.childCount === 0) {
        return null;
    }
    let index = topLevelIndexNear(doc, pos);
    for (let i = index; i >= 0; i -= 1) {
        const child = doc.child(i);
        if (child.type.name === 'paragraph' && isSceneHeadingParagraphText(child.textContent)) {
            const headingPos = positionOfTopLevelIndex(doc, i);
            const range = getSceneRange(doc, headingPos);
            if (!range || pos < range.from || pos >= range.to) {
                return null;
            }
            return headingPos;
        }
    }
    return null;
}
export function isSelectionInScriptScene(state) {
    return findSceneHeadingPosContainingPos(state.doc, state.selection.from) !== null;
}
function countWordsInParagraph(node) {
    let text = '';
    node.descendants((child) => {
        if (child.isText) {
            text += child.text || '';
            return;
        }
        if (child.type.name === 'hardBreak') {
            text += ' ';
            return;
        }
        if (child.type.name === 'scriptMention' || child.type.name === 'mention') {
            const label = typeof child.attrs.label === 'string' ? child.attrs.label : '';
            const id = typeof child.attrs.id === 'string' ? child.attrs.id : '';
            text += ` ${label || id} `;
        }
    });
    const trimmed = text.trim();
    if (!trimmed) {
        return 0;
    }
    return trimmed.split(/\s+/).filter((word) => word.length > 0).length;
}
function countDialogueWordsInRange(doc, from, to, decorations) {
    let wordCount = 0;
    doc.descendants((node, pos) => {
        if (node.type.name !== 'paragraph') {
            return;
        }
        if (pos < from || pos >= to) {
            return;
        }
        if (isSceneHeadingParagraphText(node.textContent)) {
            return;
        }
        if (isParagraphInsideFootnote(doc, pos) || isParagraphInList(doc, pos)) {
            return;
        }
        if (!paragraphHasDecorationClass(decorations, pos, node, SCRIPT_DIALOGUE_BLOCK_DOM_CLASS)) {
            return;
        }
        wordCount += countWordsInParagraph(node);
    });
    return wordCount;
}
function resolveCueDialogueDecorations(doc, decorations) {
    if (decorations != null) {
        return decorations;
    }
    return buildCharacterCueAndDialogueDecorationsForTest(doc, new Map()).decorations;
}
export function getSceneDialogueStats(doc, sceneHeadingPos, decorations) {
    const range = getSceneRange(doc, sceneHeadingPos);
    if (!range) {
        return null;
    }
    const wordCount = countDialogueWordsInRange(doc, range.from, range.to, resolveCueDialogueDecorations(doc, decorations));
    return {
        wordCount,
        estimatedMinutes: estimateDialogueMinutes(wordCount),
    };
}
export function getDialogueStatsInRange(doc, from, to, decorations) {
    if (from >= to) {
        return null;
    }
    let sceneCount = 0;
    doc.descendants((node, pos) => {
        if (pos < from || pos >= to) {
            return;
        }
        if (node.type.name === 'paragraph'
            && isSceneHeadingParagraphText(node.textContent)) {
            sceneCount += 1;
        }
    });
    if (sceneCount === 0) {
        return null;
    }
    const wordCount = countDialogueWordsInRange(doc, from, to, resolveCueDialogueDecorations(doc, decorations));
    return {
        sceneCount,
        wordCount,
        estimatedMinutes: estimateDialogueMinutes(wordCount),
    };
}
export const SCRIPT_MODE_SETTING_REFRESH_META = 'scriptModeSettingRefresh';
export const scriptCharacterCueDialoguePluginKey = new PluginKey('scriptCharacterCueDialogue');
export function getCharacterCueDialogueDecorations(state) {
    const pluginState = scriptCharacterCueDialoguePluginKey.getState(state);
    if (!pluginState) {
        return null;
    }
    return pluginState.decos;
}
function createCharacterCueDialogueDecorationPlugin(getSetting) {
    return new Plugin({
        key: scriptCharacterCueDialoguePluginKey,
        state: {
            init(_, { doc }) {
                const enabled = getSetting();
                if (!enabled) {
                    return {
                        decos: DecorationSet.empty,
                        wasActive: false,
                        dialogueEndBeforePosByCuePos: new Map(),
                    };
                }
                const { decorations, newlyDiscoveredEnds } = buildCharacterCueAndDialogueDecorations(doc, new Map());
                return {
                    decos: decorations,
                    wasActive: true,
                    dialogueEndBeforePosByCuePos: new Map(newlyDiscoveredEnds),
                };
            },
            apply(tr, value, _oldState, newState) {
                const enabled = getSetting();
                if (!enabled) {
                    return {
                        decos: DecorationSet.empty,
                        wasActive: false,
                        dialogueEndBeforePosByCuePos: new Map(),
                    };
                }
                const settingTurnedOn = !value.wasActive && enabled;
                const forceRefresh = tr.getMeta(SCRIPT_MODE_SETTING_REFRESH_META) === true;
                if (settingTurnedOn || forceRefresh) {
                    const { decorations, newlyDiscoveredEnds } = buildCharacterCueAndDialogueDecorations(newState.doc, new Map());
                    return {
                        decos: decorations,
                        wasActive: true,
                        dialogueEndBeforePosByCuePos: new Map(newlyDiscoveredEnds),
                    };
                }
                if (tr.docChanged) {
                    const mappedMap = mapDialogueEndBeforePosByCuePos(value.dialogueEndBeforePosByCuePos, tr);
                    const prunedMap = pruneDialogueEndsByCueExistence(mappedMap, newState.doc);
                    let mappedDecos = value.decos.map(tr.mapping, tr.doc);
                    if (isInPlaceTextEditThatCannotChangeScriptRoles(tr, _oldState.doc, newState.doc)) {
                        return {
                            decos: mappedDecos,
                            wasActive: true,
                            dialogueEndBeforePosByCuePos: prunedMap,
                        };
                    }
                    const splitResult = tryDecorateParagraphSplit(tr, _oldState.doc, newState.doc, mappedDecos);
                    if (splitResult) {
                        const merged = new Map(prunedMap);
                        let decos = splitResult.decorations;
                        for (const [k, v] of splitResult.newlyDiscoveredEnds) {
                            if (!merged.has(k)) {
                                merged.set(k, v);
                            }
                            decos = stripDialogueDecorationsAfterPos(newState.doc, decos, v);
                        }
                        return {
                            decos,
                            wasActive: true,
                            dialogueEndBeforePosByCuePos: merged,
                        };
                    }
                    const changedRanges = getTransactionNewDocRanges(tr);
                    if (changedRanges === 'full') {
                        const { decorations, newlyDiscoveredEnds } = buildCharacterCueAndDialogueDecorations(newState.doc, prunedMap);
                        const merged = new Map(prunedMap);
                        for (const [k, v] of newlyDiscoveredEnds) {
                            if (!merged.has(k)) {
                                merged.set(k, v);
                            }
                        }
                        return {
                            decos: decorations,
                            wasActive: true,
                            dialogueEndBeforePosByCuePos: merged,
                        };
                    }
                    if (changedRanges.length === 0) {
                        return {
                            decos: mappedDecos,
                            wasActive: true,
                            dialogueEndBeforePosByCuePos: prunedMap,
                        };
                    }
                    const { decorations, newlyDiscoveredEnds } = rebuildCharacterCueDialogueDecorationsInRanges(newState.doc, mappedDecos, prunedMap, changedRanges);
                    const merged = new Map(prunedMap);
                    let decos = decorations;
                    for (const [k, v] of newlyDiscoveredEnds) {
                        if (!merged.has(k)) {
                            merged.set(k, v);
                        }
                        decos = stripDialogueDecorationsAfterPos(newState.doc, decos, v);
                    }
                    return {
                        decos,
                        wasActive: true,
                        dialogueEndBeforePosByCuePos: merged,
                    };
                }
                return {
                    decos: value.decos.map(tr.mapping, tr.doc),
                    wasActive: true,
                    dialogueEndBeforePosByCuePos: value.dialogueEndBeforePosByCuePos,
                };
            },
        },
        props: {
            decorations(state) {
                return scriptCharacterCueDialoguePluginKey.getState(state)?.decos ?? DecorationSet.empty;
            },
        },
    });
}
function collectSceneHeadingDecorationsInRange(doc, from, to) {
    const decorations = [];
    doc.nodesBetween(from, to, (node, pos) => {
        if (node.type.name !== 'paragraph') {
            return true;
        }
        if (pos >= from && pos < to && isSceneHeadingParagraphText(node.textContent)) {
            decorations.push(Decoration.node(pos, pos + node.nodeSize, { class: SCENE_HEADING_DOM_CLASS }));
        }
        return false;
    });
    return decorations;
}
function buildSceneHeadingDecorations(doc) {
    return DecorationSet.create(doc, collectSceneHeadingDecorationsInRange(doc, 0, doc.content.size));
}
function rebuildSceneHeadingDecorationsInRanges(doc, mappedDecos, ranges) {
    let decos = mappedDecos;
    const docSize = doc.content.size;
    for (const range of ranges) {
        const expanded = expandToTopLevelBlockRange(doc, range.from, range.to);
        if (!expanded) {
            continue;
        }
        decos = removeDecorationsAnchoredInRange(decos, expanded.from, expanded.to, docSize);
        const next = collectSceneHeadingDecorationsInRange(doc, expanded.from, expanded.to);
        if (next.length > 0) {
            decos = decos.add(doc, next);
        }
    }
    return decos;
}
function createSceneHeadingDecorationPlugin(getSetting) {
    const key = new PluginKey('sceneHeadingDecorations');
    return new Plugin({
        key,
        state: {
            init(_, { doc }) {
                const enabled = getSetting();
                if (!enabled) {
                    return { decos: DecorationSet.empty, settingWasEnabled: false };
                }
                return {
                    decos: buildSceneHeadingDecorations(doc),
                    settingWasEnabled: true,
                };
            },
            apply(tr, value, _oldState, newState) {
                const enabled = getSetting();
                if (!enabled) {
                    return { decos: DecorationSet.empty, settingWasEnabled: false };
                }
                const settingTurnedOn = !value.settingWasEnabled && enabled;
                const forceRefresh = tr.getMeta(SCRIPT_MODE_SETTING_REFRESH_META) === true;
                if (settingTurnedOn || forceRefresh) {
                    return {
                        decos: buildSceneHeadingDecorations(newState.doc),
                        settingWasEnabled: true,
                    };
                }
                if (tr.docChanged) {
                    const mappedDecos = value.decos.map(tr.mapping, tr.doc);
                    if (isInPlaceTextEditThatCannotChangeScriptRoles(tr, _oldState.doc, newState.doc)) {
                        return {
                            decos: mappedDecos,
                            settingWasEnabled: true,
                        };
                    }
                    const changedRanges = getTransactionNewDocRanges(tr);
                    if (changedRanges === 'full') {
                        return {
                            decos: buildSceneHeadingDecorations(newState.doc),
                            settingWasEnabled: true,
                        };
                    }
                    if (changedRanges.length === 0) {
                        return {
                            decos: mappedDecos,
                            settingWasEnabled: true,
                        };
                    }
                    return {
                        decos: rebuildSceneHeadingDecorationsInRanges(newState.doc, mappedDecos, changedRanges),
                        settingWasEnabled: true,
                    };
                }
                return {
                    decos: value.decos.map(tr.mapping, tr.doc),
                    settingWasEnabled: true,
                };
            },
        },
        props: {
            decorations(state) {
                return key.getState(state)?.decos ?? DecorationSet.empty;
            },
        },
    });
}
export const ScriptMode = Extension.create({
    name: 'scriptMode',
    addOptions() {
        return {
            getSetting: undefined,
        };
    },
    addProseMirrorPlugins() {
        const getSetting = this.options.getSetting || (() => false);
        const plugins = [createSceneHeadingDecorationPlugin(getSetting)];
        plugins.push(createCharacterCueDialogueDecorationPlugin(getSetting));
        return plugins;
    },
});
//# sourceMappingURL=script-mode.js.map